// Shadow Network — Public profile page.
// Route: /shadow/perfil/:username
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ExternalLink, MapPin, MessageCircle, Twitter } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { tShadow } from "../i18n/shadow-social";
import { UserAvatar } from "../components/shadow/UserAvatar";
import { BadgeDisplay } from "../components/shadow/BadgeDisplay";
import { FollowButton } from "../components/shadow/FollowButton";
import { TipJarWidget } from "../components/shadow/TipJarWidget";
import { SubscribeButton } from "../components/shadow/SubscribeButton";
import { AddToListButton } from "../components/shadow/AddToListButton";
import { CopyProfileUrlButton } from "../components/shadow/CopyProfileUrlButton";
import AirdropHistory from "../components/profile/AirdropHistory";
// Runtime-first API base. In production (*.rebelbtc.com) this resolves to
// window.location.origin so the request stays same-origin and Vercel rewrites
// /api/* to the Emergent backend. Using REACT_APP_BACKEND_URL directly would
// bake the preview URL into the production bundle and cause a cross-origin
// GET that hangs forever → black screen.
import { API_BASE as API } from "../lib/api";

const Stat = ({ label, value }) => (
  <div className="flex flex-col items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur">
    <span className="text-xl font-black text-white tabular-nums md:text-2xl">{value}</span>
    <span className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/45">
      {label}
    </span>
  </div>
);

const ShadowProfilePage = () => {
  const { username } = useParams();
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/api/shadow/social/profile/${username}`)
      .then((res) => {
        if (mounted) setProfile(res.data);
      })
      .catch((e) => {
        if (mounted) setError(e.response?.status === 404 ? "not_found" : "error");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [username]);

  const isMe = useMemo(
    () => user && profile?.user && user.id === profile.user.id,
    [user, profile]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-white/45 text-sm">…</p>
      </div>
    );
  }
  if (error === "not_found") {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-sm text-white/70 mb-4">Operator not found.</p>
          <Link to="/shadow" className="text-amber-400 underline">
            ← Shadow
          </Link>
        </div>
      </div>
    );
  }
  // FIX: never return `null` on a network/CORS error — that produced a fully
  // black page in production. Show a clear, actionable fallback instead.
  if (error || !profile || !profile.user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <p className="text-amber-400 text-sm font-mono mb-2">@{username}</p>
          <p className="text-sm text-white/65 mb-4">
            No se pudo cargar este perfil ahora mismo. Si crees que es un error,
            recarga la página o vuelve más tarde.
          </p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="rounded-full border border-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] hover:border-amber-400/60"
            >
              Recargar
            </button>
            <Link
              to="/shadow"
              className="rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-amber-300"
            >
              ← Shadow
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const u = profile.user;
  const stats = profile.stats || {};
  const badges = profile.badges || [];
  const analyses = profile.analyses || [];
  const handle = u.username ? `@${u.username}` : u.operator_codename;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="profile-page">
      {/* Top header */}
      <header className="border-b border-white/5 bg-black/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-3 md:px-8">
          <Link
            to="/shadow"
            className="inline-flex items-center gap-2 text-white/60 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">Shadow</span>
          </Link>
          <p className="truncate text-xs uppercase tracking-[0.18em] text-white/45">
            {handle || u.name}
          </p>
          <span className="w-20" />
        </div>
      </header>

      <main className="mx-auto max-w-4xl">
        {/* Banner */}
        <div
          data-testid="profile-banner"
          className="relative h-48 w-full overflow-hidden bg-gradient-to-br from-amber-500/15 via-fuchsia-500/10 to-cyan-400/15 md:h-64"
          style={
            u.banner_url
              ? { backgroundImage: `url(${u.banner_url})`, backgroundSize: "cover", backgroundPosition: "center" }
              : undefined
          }
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        </div>

        {/* Hero info */}
        <section className="relative px-4 pb-4 md:px-8">
          <div className="-mt-16 flex flex-col gap-4 md:-mt-20 md:flex-row md:items-end md:justify-between">
            <div className="flex items-end gap-4">
              <div className="rounded-full border-4 border-black">
                <UserAvatar user={u} size="xl" />
              </div>
              <div className="pb-2">
                <h1
                  data-testid="profile-name"
                  className="text-xl font-black text-white md:text-2xl"
                >
                  {u.name}
                </h1>
                {handle && (
                  <p className="text-xs text-white/55 mt-0.5">{handle}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isMe ? (
                <Link
                  to="/shadow/perfil/editar"
                  data-testid="profile-edit-cta"
                  className="inline-flex items-center justify-center rounded-full border border-white/20 px-5 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white transition-all hover:border-amber-400/60 hover:text-amber-300"
                >
                  {tShadow("editarPerfil", lang)}
                </Link>
              ) : (
                <>
                  <FollowButton
                    targetUserId={u.id}
                    initialFollowing={profile.is_following}
                  />
                  {/* Message button — visible for every signed-in operator
                      (FREE & PRO). If not signed in, the link will land on
                      the chat page which already prompts to authenticate. */}
                  <Link
                    to={`/shadow/mensajes/${u.id}`}
                    data-testid="profile-message-btn"
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-white hover:border-amber-400/60 hover:text-amber-300 transition-all"
                    title={tShadow("enviarMensaje", lang)}
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{tShadow("mensajes", lang)}</span>
                  </Link>
                  <SubscribeButton username={u.username} />
                  <AddToListButton targetUsername={u.username} />
                </>
              )}
              {u.username && (
                <CopyProfileUrlButton username={u.username} variant="icon" />
              )}
            </div>
          </div>

          {/* Bio + meta */}
          {u.bio && (
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/75">
              {u.bio}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/55">
            {u.location && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                {u.location}
              </span>
            )}
            {u.website && (
              <a
                href={u.website.startsWith("http") ? u.website : `https://${u.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-cyan-300 hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {u.website}
              </a>
            )}
            {u.twitter_handle && (
              <a
                href={`https://twitter.com/${u.twitter_handle.replace(/^@/, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-cyan-300 hover:underline"
              >
                <Twitter className="h-3.5 w-3.5" />@{u.twitter_handle.replace(/^@/, "")}
              </a>
            )}
            <span>
              <strong className="text-white/85">{profile.followers_count}</strong>{" "}
              {tShadow("seguidores", lang)}
            </span>
            <span>
              <strong className="text-white/85">{profile.following_count}</strong>{" "}
              {tShadow("siguiendoN", lang)}
            </span>
          </div>

          {/* Badges */}
          {badges.length > 0 && (
            <div className="mt-4">
              <BadgeDisplay badges={badges} max={8} size="md" />
            </div>
          )}
        </section>

        {/* Stats */}
        <section className="mt-4 px-4 md:px-8">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label={tShadow("analisis", lang)} value={stats.total_analyses || 0} />
            <Stat
              label={tShadow("winRate", lang)}
              value={`${Math.round(stats.win_rate || 0)}%`}
            />
            <Stat label={tShadow("racha", lang)} value={stats.streak || 0} />
            <Stat
              label={tShadow("mejorCall", lang)}
              value={stats.best_call ? "✓" : "—"}
            />
          </div>
        </section>

        {/* Tip Jar (only shown when the operator has set an address) */}
        {u.tip_address && (
          <section className="mt-6 px-4 md:px-8">
            <TipJarWidget address={u.tip_address} chain={u.tip_chain || "base"} />
          </section>
        )}

        {/* Recent analyses */}
        <section className="mt-8 px-4 pb-12 md:px-8">
          <h2 className="mb-4 text-[10px] font-bold uppercase tracking-[0.25em] text-white/45">
            {tShadow("analisis", lang)}
          </h2>
          {analyses.length === 0 ? (
            <p className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center text-white/45">
              —
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {analyses.map((a) => (
                <Link
                  key={a.id}
                  to={`/shadow#analysis-${a.id}`}
                  data-testid={`profile-analysis-${a.id}`}
                  className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition-all hover:border-amber-400/40"
                >
                  {(a.image_url || a.video_url) && (
                    <div className="aspect-video w-full overflow-hidden bg-black">
                      <img
                        src={a.image_url || a.video_url}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-3">
                    <p className="line-clamp-2 text-sm font-semibold text-white">{a.title}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-white/40">
                      {a.ticker || "—"} · {a.direction || "—"}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default ShadowProfilePage;
